'use client';

/**
 * LUCAT CONNECT — Accueil Essentiel
 * Grille de 6 grandes vignettes : Marie, Jean, Sophie, Radio, Nouvelles, Ma journée
 * Format 1280×800 paysage, sans scroll, accessibilité senior.
 * Retour visuel tactile complet sur tous les boutons et cartes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { useRadio } from '@/lib/GlobalRadioContext';
import {
  getActiveNews, markDeferred, updateNewsStatus,
  getNotificationText, getNotificationPrimaryLabel, getNotificationHref,
  contactsById,
  type SocialNewsItem,
} from '@/lib/socialNews';
import { CONTACTS } from '@/lib/contacts';
import { getPriorityStations, type RadioStation } from '@/lib/radioStations';

// ── Priority contacts config ─────────────────────────────────────────────────
const priorityContactIds: string[] = ['marie', 'jean', 'sophie'];
const priorityContacts = priorityContactIds
  .map(id => CONTACTS.find(c => c.id === id))
  .filter(Boolean) as typeof CONTACTS;

// ── Palette constantes ──────────────────────────────────────────────────────
const TEAL = {
  bg: '#E8F4F1',
  border: '#69A999',
  textMain: '#173E36',
  btnPrimary: '#247B68',
  badge: '#176F5B',
};

const AGENDA_ACCENT = '#3B82F6';

// ── Haptic helper ────────────────────────────────────────────────────────────
function triggerHaptic() {
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(20);
    }
  } catch { /* ignore */ }
}

// ── usePressState hook ───────────────────────────────────────────────────────
function usePressState(minFeedbackMs = 180, safetyMs = 2500) {
  const [pressed, setPressed] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePress = useCallback((action: () => void, isNav = false) => {
    if (loading) return;
    triggerHaptic();
    setPressed(true);
    timerRef.current = setTimeout(() => {
      setPressed(false);
      if (isNav) setLoading(true);
      action();
      if (isNav) {
        setTimeout(() => setLoading(false), safetyMs);
      }
    }, minFeedbackMs);
  }, [loading, minFeedbackMs, safetyMs]);

  const reset = useCallback(() => {
    setPressed(false);
    setLoading(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { pressed, loading, handlePress, reset };
}

// ── Contact Card ────────────────────────────────────────────────────────────
function ContactCard({ contact }: { contact: typeof CONTACTS[0] }) {
  const router = useRouter();
  const [callState, setCallState] = useState<'idle' | 'pressed' | 'loading'>('idle');
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCall = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (callState !== 'idle') return;
    triggerHaptic();
    setCallState('pressed');
    timerRef.current = setTimeout(() => {
      setCallState('loading');
      setTimeout(() => {
        router.push(`/call-confirmation-screen?contact=${contact.id}`);
      }, 80);
    }, 180);
  }, [callState, contact.id, router]);

  const isPressed = callState === 'pressed';
  const isLoading = callState === 'loading';

  return (
    <div
      onClick={handleCall}
      role="button"
      tabIndex={0}
      aria-label={`Appeler ${contact.firstName} — ${contact.relationship}`}
      onKeyDown={e => e.key === 'Enter' && handleCall(e as unknown as React.MouseEvent)}
      className="interactive-card"
      style={{
        background: '#FFFFFF',
        border: '2.5px solid #E5E7EB',
        borderRadius: '20px',
        padding: '20px 16px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        boxShadow: '0 3px 16px rgba(26,39,68,0.08)',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Portrait circulaire */}
      <div style={{
        width: 'clamp(100px, 10vw, 130px)',
        height: 'clamp(100px, 10vw, 130px)',
        borderRadius: '50%',
        overflow: 'hidden',
        border: '3.5px solid #5DBB1F',
        flexShrink: 0,
      }}>
        <AppImage
          src={contact.photo}
          alt={contact.photoAlt}
          width={130}
          height={130}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Prénom + lien familial */}
      <div style={{ textAlign: 'center', minWidth: 0, width: '100%' }}>
        <div style={{
          fontSize: 'clamp(28px, 3vw, 34px)',
          fontWeight: 900,
          color: '#1A2744',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {contact.firstName}
        </div>
        <div style={{
          fontSize: 'clamp(18px, 2vw, 22px)',
          fontWeight: 600,
          color: '#5A6A7A',
          marginTop: '2px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {contact.relationship}
        </div>
      </div>

      {/* Bouton Appeler */}
      <button
        onClick={handleCall}
        aria-label={isLoading ? `Appel de ${contact.firstName} en préparation…` : `Appeler ${contact.firstName}`}
        aria-busy={isLoading}
        disabled={callState !== 'idle'}
        className="interactive-btn"
        style={{
          width: '100%',
          background: isPressed
            ? 'linear-gradient(135deg, #3D9A10 0%, #2D7A08 100%)'
            : isLoading
            ? 'linear-gradient(135deg, #4DA010 0%, #2D7A08 100%)'
            : 'linear-gradient(135deg, #5DBB1F 0%, #3D9A10 100%)',
          border: isPressed ? '2px solid #2D7A08' : '2px solid transparent',
          borderRadius: '14px',
          minHeight: '72px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          cursor: callState !== 'idle' ? 'not-allowed' : 'pointer',
          boxShadow: isPressed || isLoading ? 'none' : '0 4px 14px rgba(93,187,31,0.35)',
          marginTop: 'auto',
          flexShrink: 0,
          transform: isPressed ? 'translateY(1px) scale(0.97)' : 'scale(1)',
          filter: isPressed ? 'brightness(0.92)' : 'none',
          transition: 'transform 120ms ease, background 120ms ease, box-shadow 120ms ease, border-color 120ms ease, filter 120ms ease',
        }}
      >
        {isLoading ? (
          <>
            <div style={{
              width: '20px', height: '20px', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.8)',
              borderTopColor: 'transparent',
              animation: 'sab-spin 0.7s linear infinite',
              flexShrink: 0,
            }} aria-hidden="true" />
            <span style={{ fontSize: 'clamp(16px, 1.8vw, 20px)', fontWeight: 800, color: 'white' }}>
              Appel en préparation…
            </span>
          </>
        ) : (
          <>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 800, color: 'white' }}>
              Appeler
            </span>
          </>
        )}
      </button>
      <style>{`@keyframes sab-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Radio Card ──────────────────────────────────────────────────────────────
function EssentialRadioCard() {
  const { playState, selectedStationId, selectAndPlay, pause, resume, stop } = useRadio();
  const [stationLoadingId, setStationLoadingId] = useState<string | null>(null);
  const [stopPressed, setStopPressed] = useState(false);

  const isPlaying = playState === 'playing' || playState === 'fallback';
  const isPaused = playState === 'paused';
  const isLoading = playState === 'loading';
  const isActive = isPlaying || isPaused || isLoading;

  const priorityStations = getPriorityStations(3);

  const handleStationPress = useCallback((station: RadioStation) => {
    if (stationLoadingId) return;
    triggerHaptic();
    if (selectedStationId === station.id) {
      if (isPlaying) {
        pause();
      } else if (isPaused) {
        resume();
      } else {
        setStationLoadingId(station.id);
        selectAndPlay(station.id);
        setTimeout(() => setStationLoadingId(null), 2500);
      }
    } else {
      setStationLoadingId(station.id);
      selectAndPlay(station.id);
      setTimeout(() => setStationLoadingId(null), 2500);
    }
  }, [stationLoadingId, selectedStationId, isPlaying, isPaused, pause, resume, selectAndPlay]);

  // Clear loading state when play state changes
  useEffect(() => {
    if (playState === 'playing' || playState === 'fallback' || playState === 'paused') {
      setStationLoadingId(null);
    }
  }, [playState]);

  const handleStop = useCallback(() => {
    if (stopPressed) return;
    triggerHaptic();
    setStopPressed(true);
    setTimeout(() => {
      stop();
      setStopPressed(false);
    }, 150);
  }, [stopPressed, stop]);

  const activeStationName = isActive && selectedStationId
    ? priorityStations.find(s => s.id === selectedStationId)?.name ?? selectedStationId
    : null;

  const statusText = activeStationName
    ? `${activeStationName} ${isPlaying ? 'est en cours de lecture' : isPaused ? 'en pause' : 'en chargement…'}`
    : 'Choisissez une station';

  return (
    <div style={{
      background: '#FFF8EC',
      border: `2.5px solid rgba(245,166,35,0.4)`,
      borderRadius: '20px',
      padding: '16px 16px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      overflow: 'hidden',
      minHeight: 0,
    }}>
      {/* Header — titre à gauche, bouton Arrêter à droite */}
      <div style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '8px',
        minHeight: '56px',
      }}>
        {/* Titre + sous-titre */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'clamp(22px, 2.4vw, 28px)', fontWeight: 900, color: '#1A2744', lineHeight: 1.1 }}>
            Radio
          </div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#9CA3AF', marginTop: '2px', lineHeight: 1.3 }}>
            {statusText}
          </div>
        </div>

        {/* Bouton Arrêter — affiché uniquement si une station est active */}
        {isActive && (
          <button
            onClick={handleStop}
            aria-label="Arrêter la radio"
            title="Arrêter la radio"
            disabled={stopPressed}
            className="interactive-btn"
            style={{
              flexShrink: 0,
              minWidth: '56px',
              minHeight: '56px',
              background: stopPressed ? '#FCA5A5' : '#FEE2E2',
              border: stopPressed ? '2px solid #B91C1C' : '1.5px solid rgba(185,28,28,0.35)',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer',
              padding: '0 12px',
              outline: 'none',
              transform: stopPressed ? 'translateY(1px) scale(0.97)' : 'scale(1)',
              filter: stopPressed ? 'brightness(0.92)' : 'none',
              transition: 'transform 120ms ease, background 120ms ease, box-shadow 120ms ease, border-color 120ms ease, filter 120ms ease',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#B91C1C" stroke="none" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
            <span style={{ fontSize: '15px', fontWeight: 700, color: '#B91C1C', whiteSpace: 'nowrap' }}>
              Arrêter
            </span>
          </button>
        )}
      </div>

      {/* Station buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minHeight: 0 }}>
        {priorityStations.map(station => {
          const isThisActive = selectedStationId === station.id;
          const isThisPlaying = isThisActive && isPlaying;
          const isThisPaused = isThisActive && isPaused;
          const isThisLoading = (isThisActive && isLoading) || stationLoadingId === station.id;
          const isThisDisabled = !!(stationLoadingId && stationLoadingId !== station.id);

          const btnBg = isThisPlaying
            ? station.color
            : isThisPaused
            ? station.bg
            : '#FFFFFF';
          const btnBorder = isThisActive
            ? `2px solid ${station.color}`
            : '2px solid #E5E7EB';
          const textColor = isThisPlaying ? '#FFFFFF' : '#1A2744';
          const iconColor = isThisPlaying ? '#FFFFFF' : station.color;

          let ariaLabel: string;
          if (isThisLoading) ariaLabel = `Chargement de ${station.name}…`;
          else if (isThisPlaying) ariaLabel = `Mettre ${station.name} en pause`;
          else if (isThisPaused) ariaLabel = `Reprendre ${station.name}`;
          else ariaLabel = `Écouter ${station.name}`;

          return (
            <button
              key={station.id}
              onClick={() => handleStationPress(station)}
              aria-label={ariaLabel}
              aria-pressed={isThisActive}
              aria-busy={isThisLoading}
              disabled={isThisDisabled}
              className="interactive-btn"
              style={{
                width: '100%',
                minHeight: '64px',
                background: btnBg,
                border: btnBorder,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '0 14px',
                cursor: isThisDisabled ? 'not-allowed' : 'pointer',
                boxShadow: isThisActive ? `0 3px 12px ${station.color}44` : '0 1px 4px rgba(0,0,0,0.06)',
                flexShrink: 0,
                outline: 'none',
                opacity: isThisDisabled ? 0.5 : 1,
              }}
            >
              <svg
                width="26" height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={iconColor}
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
                aria-hidden="true"
              >
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
                <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
              </svg>

              <span style={{
                flex: 1,
                fontSize: 'clamp(16px, 1.7vw, 20px)',
                fontWeight: 800,
                color: textColor,
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {isThisPaused ? `▶ Reprendre ${station.name}` : station.name}
                {isThisLoading && !isThisPlaying && !isThisPaused ? ' — Chargement…' : ''}
              </span>

              {isThisLoading && !isThisPlaying ? (
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%',
                  border: `3px solid ${station.color}`,
                  borderTopColor: 'transparent',
                  animation: 'sab-spin 0.8s linear infinite',
                  flexShrink: 0,
                }} aria-hidden="true" />
              ) : isThisPlaying ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill={iconColor} stroke={iconColor} strokeWidth="1" style={{ flexShrink: 0 }} aria-hidden="true">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill={iconColor} stroke="none" style={{ flexShrink: 0 }} aria-hidden="true">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes sab-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── Nouvelles Card ──────────────────────────────────────────────────────────
function EssentialNewsCard() {
  const router = useRouter();
  const [current, setCurrent] = useState<SocialNewsItem | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [actionState, setActionState] = useState<'idle' | 'loading'>('idle');
  const [deferPressed, setDeferPressed] = useState(false);
  const [seeAllPressed, setSeeAllPressed] = useState(false);

  const refresh = useCallback(() => {
    const active = getActiveNews();
    setActiveCount(active.length);
    setCurrent(active[0] ?? null);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDefer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!current || deferPressed) return;
    triggerHaptic();
    setDeferPressed(true);
    setTimeout(() => {
      markDeferred(current.id);
      refresh();
      setDeferPressed(false);
    }, 180);
  };

  const handleAction = () => {
    if (!current || actionState !== 'idle') return;
    triggerHaptic();
    setActionState('loading');
    setTimeout(() => {
      if (current.status === 'unread') updateNewsStatus(current.id, 'seen');
      router.push(getNotificationHref(current));
    }, 200);
  };

  const handleSeeAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (seeAllPressed) return;
    triggerHaptic();
    setSeeAllPressed(true);
    setTimeout(() => {
      router.push('/actualites');
    }, 180);
  };

  const contact = current ? contactsById[current.contactId] : null;
  const displayText = current ? getNotificationText(current) : '';
  const primaryLabel = current ? getNotificationPrimaryLabel(current) : '';

  return (
    <div style={{
      background: TEAL.bg,
      border: `2.5px solid ${TEAL.border}`,
      borderRadius: '20px',
      padding: '20px 16px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      overflow: 'hidden',
      minHeight: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexShrink: 0 }}>
        <div style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 800, color: TEAL.textMain, lineHeight: 1.2 }}>
          Nouvelles de mes proches
        </div>
        {activeCount > 0 && (
          <div style={{
            background: TEAL.badge, color: 'white',
            borderRadius: '20px', padding: '3px 12px',
            fontSize: '14px', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {activeCount}
          </div>
        )}
      </div>

      {current && contact ? (
        <>
          {/* Contact row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              overflow: 'hidden', border: `2.5px solid ${TEAL.border}`, flexShrink: 0,
            }}>
              <AppImage src={contact.photo} alt={contact.photoAlt} width={56} height={56}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'clamp(16px, 1.8vw, 20px)', fontWeight: 700, color: TEAL.textMain, lineHeight: 1.3 }}>
                {displayText}
              </div>
              <div style={{ fontSize: '14px', color: '#5A7A74', marginTop: '2px' }}>
                {current.date}
              </div>
            </div>
          </div>

          {/* Primary action */}
          <button
            onClick={handleAction}
            aria-label={actionState === 'loading' ? 'Ouverture…' : primaryLabel}
            aria-busy={actionState === 'loading'}
            disabled={actionState !== 'idle'}
            className="interactive-btn"
            style={{
              width: '100%',
              background: actionState === 'loading' ? '#176F5B' : TEAL.btnPrimary,
              border: '2px solid transparent',
              borderRadius: '14px',
              minHeight: '72px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              cursor: actionState !== 'idle' ? 'not-allowed' : 'pointer',
              marginTop: 'auto',
              flexShrink: 0,
              boxShadow: actionState === 'loading' ? 'none' : '0 3px 10px rgba(36,123,104,0.25)',
            }}
          >
            {actionState === 'loading' && (
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%',
                border: '3px solid rgba(255,255,255,0.8)',
                borderTopColor: 'transparent',
                animation: 'sab-spin 0.7s linear infinite',
                flexShrink: 0,
              }} aria-hidden="true" />
            )}
            <span style={{ fontSize: 'clamp(17px, 1.9vw, 21px)', fontWeight: 800, color: 'white' }}>
              {actionState === 'loading' ? 'Ouverture…' : primaryLabel}
            </span>
          </button>

          {/* Secondary row */}
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={handleDefer}
              aria-label="Reporter à plus tard"
              disabled={deferPressed}
              className="interactive-btn"
              style={{
                flex: 1,
                background: deferPressed ? '#FFF3E0' : 'white',
                border: `1.5px solid ${deferPressed ? '#F5A623' : TEAL.border}`,
                borderRadius: '10px',
                minHeight: '48px',
                fontSize: '15px',
                fontWeight: 700,
                color: TEAL.btnPrimary,
                cursor: deferPressed ? 'not-allowed' : 'pointer',
              }}
            >
              Plus tard
            </button>
            <button
              onClick={handleSeeAll}
              aria-label="Voir toutes les nouvelles"
              disabled={seeAllPressed}
              className="interactive-btn"
              style={{
                flex: 1,
                background: seeAllPressed ? '#E8F4F1' : 'white',
                border: `1.5px solid ${TEAL.border}`,
                borderRadius: '10px',
                minHeight: '48px',
                fontSize: '15px',
                fontWeight: 700,
                color: TEAL.textMain,
                cursor: seeAllPressed ? 'not-allowed' : 'pointer',
              }}
            >
              {seeAllPressed ? 'Ouverture…' : 'Voir tout'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '32px' }}>✓</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: TEAL.textMain, textAlign: 'center' }}>
              Vous n'avez aucune nouvelle à consulter.
            </div>
          </div>
          <button
            onClick={handleSeeAll}
            aria-label="Voir toutes les nouvelles"
            disabled={seeAllPressed}
            className="interactive-btn"
            style={{
              width: '100%',
              background: seeAllPressed ? '#E8F4F1' : 'white',
              border: `1.5px solid ${TEAL.border}`,
              borderRadius: '14px',
              minHeight: '64px',
              fontSize: '18px',
              fontWeight: 700,
              color: TEAL.btnPrimary,
              cursor: seeAllPressed ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            {seeAllPressed ? 'Ouverture…' : 'Voir mes actualités'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Ma Journée Card ─────────────────────────────────────────────────────────
function EssentialAgendaCard() {
  const router = useRouter();
  const [readState, setReadState] = useState<'idle' | 'loading'>('idle');

  const nextEvent = {
    time: '15h00',
    label: 'Visite de Marie',
    contactId: 'marie',
  };

  const contact = contactsById[nextEvent.contactId];

  const handleRead = useCallback(() => {
    if (readState !== 'idle') return;
    triggerHaptic();
    setReadState('loading');
    setTimeout(() => {
      router.push('/ma-journee');
    }, 200);
  }, [readState, router]);

  return (
    <div style={{
      background: '#EFF6FF',
      border: `2.5px solid rgba(59,130,246,0.35)`,
      borderRadius: '20px',
      padding: '20px 16px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      overflow: 'hidden',
      minHeight: 0,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={AGENDA_ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <div style={{ fontSize: 'clamp(22px, 2.4vw, 28px)', fontWeight: 900, color: '#1A2744', lineHeight: 1.1 }}>
          Ma journée
        </div>
      </div>

      {/* Next event */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <div style={{
          fontSize: 'clamp(36px, 4vw, 44px)',
          fontWeight: 900,
          color: AGENDA_ACCENT,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}>
          {nextEvent.time}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'clamp(18px, 2vw, 24px)', fontWeight: 700, color: '#1A2744', lineHeight: 1.2 }}>
            {nextEvent.label}
          </div>
        </div>
        {contact && (
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            overflow: 'hidden', border: `2.5px solid ${AGENDA_ACCENT}`, flexShrink: 0,
          }}>
            <AppImage src={contact.photo} alt={contact.photoAlt} width={52} height={52}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
      </div>

      {/* Primary button */}
      <button
        onClick={handleRead}
        aria-label={readState === 'loading' ? 'Ouverture de l\'agenda…' : 'Lire ma journée'}
        aria-busy={readState === 'loading'}
        disabled={readState !== 'idle'}
        className="interactive-btn"
        style={{
          width: '100%',
          background: readState === 'loading' ? '#2563EB' : AGENDA_ACCENT,
          border: '2px solid transparent',
          borderRadius: '14px',
          minHeight: '72px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          cursor: readState !== 'idle' ? 'not-allowed' : 'pointer',
          marginTop: 'auto',
          flexShrink: 0,
          boxShadow: readState === 'loading' ? 'none' : '0 3px 12px rgba(59,130,246,0.3)',
        }}
      >
        {readState === 'loading' ? (
          <>
            <div style={{
              width: '20px', height: '20px', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.8)',
              borderTopColor: 'transparent',
              animation: 'sab-spin 0.7s linear infinite',
              flexShrink: 0,
            }} aria-hidden="true" />
            <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 800, color: 'white' }}>
              Ouverture de l'agenda…
            </span>
          </>
        ) : (
          <>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
            <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 800, color: 'white' }}>Lire ma journée</span>
          </>
        )}
      </button>
    </div>
  );
}

// ── Section Bar — Mes proches prioritaires ──────────────────────────────────
function ProchesBar() {
  const router = useRouter();
  const [navPressed, setNavPressed] = useState(false);

  const handleSeeAll = useCallback(() => {
    if (navPressed) return;
    triggerHaptic();
    setNavPressed(true);
    setTimeout(() => {
      router.push('/mes-proches?from=essential');
    }, 180);
  }, [navPressed, router]);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '0 clamp(20px, 2vw, 28px)',
      flexShrink: 0,
      minHeight: '56px',
    }}>
      {/* Left: icon + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <svg
          width="28" height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#1A2744"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span style={{
          fontSize: 'clamp(20px, 2.2vw, 24px)',
          fontWeight: 800,
          color: '#1A2744',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}>
          Mes proches prioritaires
        </span>
      </div>

      {/* Right: "Voir tous mes proches" button */}
      <button
        onClick={handleSeeAll}
        aria-label="Voir tous mes proches"
        disabled={navPressed}
        className="interactive-btn"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: navPressed ? '#EFF6FF' : '#FFFFFF',
          border: navPressed ? '2px solid #1A2744' : '2px solid #2D3F6B',
          borderRadius: '14px',
          padding: '0 18px',
          minHeight: '56px',
          cursor: navPressed ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          boxShadow: navPressed ? 'none' : '0 2px 8px rgba(26,39,68,0.08)',
          outline: 'none',
        }}
      >
        {navPressed ? (
          <>
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%',
              border: '2.5px solid #2D3F6B',
              borderTopColor: 'transparent',
              animation: 'sab-spin 0.7s linear infinite',
              flexShrink: 0,
            }} aria-hidden="true" />
            <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 700, color: '#1A2744', whiteSpace: 'nowrap' }}>
              Ouverture…
            </span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 700, color: '#1A2744', whiteSpace: 'nowrap' }}>
              Voir tous mes proches
            </span>
            <svg
              width="22" height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1A2744"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ flexShrink: 0 }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
}

// ── Main Essential Home ─────────────────────────────────────────────────────
export default function EssentialHomeScreen() {
  const padH = 'clamp(20px, 2vw, 28px)';
  const padV = 'clamp(10px, 1.4vh, 18px)';
  const gap = 'clamp(12px, 1.6vw, 18px)';

  return (
    <main
      aria-label="Accueil essentiel"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: gap,
        paddingTop: padV,
        paddingBottom: padV,
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Section bar: Mes proches prioritaires */}
      <ProchesBar />

      {/* Contact cards row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${priorityContacts.length}, 1fr)`,
        gap: gap,
        padding: `0 ${padH}`,
        flex: '1 1 0',
        minHeight: 0,
      }}>
        {priorityContacts.map(contact => (
          <ContactCard key={contact.id} contact={contact} />
        ))}
      </div>

      {/* Service cards row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: gap,
        padding: `0 ${padH}`,
        flex: '1 1 0',
        minHeight: 0,
      }}>
        <EssentialRadioCard />
        <EssentialNewsCard />
        <EssentialAgendaCard />
      </div>
    </main>
  );
}
